/* eslint no-underscore-dangle: ["error", { "allow": [ "_id"] }] */
import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridActionsCellItem, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSkeleton from '../../utils/LoadingSkeleton';
import NoRowsOverlay from '../../utils/NoRowOverlay';
import UOMAdd from './add';
import UOMEdit from './edit';
import UOMView from './view';
import UOMDelete from './delete';
import UomAction from '../../store/actions/UomAction';
import CopyInput from '../../components/CopyInput';
import useHasPermission from '../../utils/useHasPermission';
import { uom, show, store, update, destroy } from '../../utils/constants';

function QuickSearchToolbar() {
  const [addOpen, setAddOpen] = useState(false);

  return (
    <Box
      className="tableTopWrap page__title"
      sx={{
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 3
      }}
    >
      <Stack direction="row" sx={{ ml: 'auto' }} spacing={2} className="MuiDataGrid-Search">
        <GridToolbarQuickFilter
          quickFilterParser={(searchInput) => searchInput.split(',').map((value) => value.trim())}
          quickFilterFormatter={(quickFilterValues) => quickFilterValues.join(', ')}
          debounceMs={800} // time before applying the new quick filter value
          variant="outlined"
          size="small"
          sx={{ pb: 0, maxWidth: '275px' }}
        />
        {useHasPermission(uom, store) && (
          <Button
            className="button_primary"
            variant="contained"
            size="small"
            color="secondary"
            onClick={() => {
              setAddOpen(true);
            }}
          >
            Add UOM
          </Button>
        )}
      </Stack>
      {addOpen === true ? (
        <UOMAdd openmodal={addOpen} onCloseModal={() => setAddOpen(false)} />
      ) : (
        ''
      )}
    </Box>
  );
}

export default function UOMList() {
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10
  });

  const [sortInfo, setSortInfo] = React.useState({
    sortField: '',
    sort: ''
  });

  const [searchKey, setSearchKey] = React.useState('');
  const [viewOpen, setViewOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  const [itemID, setItemID] = React.useState('');

  const [rowCountState, setRowCountState] = React.useState(0);

  const [data, setData] = React.useState([]);

  const dispatch = useDispatch();

  const { uomData, isLoading } = useSelector((state) => state.uom); // Replace 'data' with your reducer name
  const { paginationData, sortingData, keyword } = useSelector((state) => state.uom.queryParams);

  useEffect(() => {
    dispatch(UomAction.updateQueryParams(paginationModel, sortInfo, searchKey));
    dispatch(UomAction.getAll(paginationModel, sortInfo, searchKey));
  }, [dispatch, paginationModel, sortInfo, searchKey]);

  useEffect(() => {
    if (uomData) {
      if (uomData.data) {
        setData(uomData.data);
      } else {
        setData([]);
      }
      setRowCountState((prevRowCountState) =>
        uomData.total !== undefined ? uomData.total : prevRowCountState
      );
    }
  }, [uomData]);

  useEffect(() => {
    if (paginationData) {
      setPaginationModel(paginationData);
    }
    if (sortingData) {
      setSortInfo(sortingData);
    }
    if (keyword) {
      setSearchKey(keyword);
    }
  }, [paginationData, keyword, sortingData]);

  const columns = [
    {
      field: 'unique_id',
      headerName: '',
      sortable: false,
      align: 'left',
      flex: 0.3,
      renderCell: (params) => {
        if (params && params.value) {
          return <CopyInput id={params.value} />;
        }
        return null;
      }
    },
    {
      field: 'name',
      headerName: 'UOM Name',
      flex: 1.25
    },
    {
      field: 'short_name',
      headerName: 'UOM Short Name',
      flex: 1.25
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      renderCell: (params) => {
        if (params && params.value) {
          return params.value === 1 ? (
            <Chip label="Active" color="success" />
          ) : (
            <Chip label="Inactive" color="lignhtwarning" />
          );
        }
        return null;
      }
    },
    useHasPermission(uom) && {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 1,
      getActions: (params) => [
        uom && useHasPermission(uom, show) ? (
          <GridActionsCellItem
            key={0}
            icon={<i className="icon-view" />}
            className="actionBtn"
            label="View"
            title="View"
            onClick={() => {
              setViewOpen(true);
              setItemID(params.id);
            }}
          />
        ) : (
          <span />
        ),
        uom && useHasPermission(uom, update) ? (
          <GridActionsCellItem
            key={1}
            icon={<i className="icon-edit" />}
            className="actionBtn"
            label="Edit"
            title="Edit"
            onClick={() => {
              setEditOpen(true);
              setItemID(params.id);
            }}
          />
        ) : (
          <span />
        ),
        uom && useHasPermission(uom, destroy) ? (
          <GridActionsCellItem
            key={2}
            icon={<i className="icon-delete" />}
            className="actionBtn"
            title="Delete"
            label="Delete"
            onClick={() => {
              setDeleteOpen(true);
              setItemID(params.id);
            }}
            // showInMenu
          />
        ) : (
          <span />
        )
      ]
    }
  ];

  const handleSortModelChange = React.useCallback((sortModel) => {
    // Here you save the data you need from the sort model
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      const sortData = {
        sortField: field,
        sort
      };
      setSortInfo(sortData);
    }
    // eslint-disable-next-line
  }, []);

  const onFilterChange = React.useCallback((filterModel) => {
    // Here you save the data you need from the filter model
    const keywordData = filterModel.quickFilterValues[0];
    setSearchKey(keywordData);
    setPaginationModel({ page: 0, pageSize: 10 });
    // eslint-disable-next-line
  }, []);

  return (
    <Box sx={{ mb: 3 }} className="cardWrapper">
      <div className="pageTitleWrap">
        <Typography className="" component="h2" variant="h2">
          Unit of Measurements
        </Typography>
      </div>
      <Box sx={{ bgcolor: 'white' }} className="dataTable__wrap">
        <DataGrid
          rows={data}
          getRowId={(row) => row._id}
          rowCount={rowCountState}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10
              }
            }
          }}
          autoHeight
          sx={{ minHeight: 650, '--DataGrid-overlayHeight': '50vh' }}
          disableColumnFilter
          rowHeight={50}
          slots={{
            toolbar: QuickSearchToolbar,
            loadingOverlay: LoadingSkeleton,
            noRowsOverlay: NoRowsOverlay
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          filterMode="server"
          onFilterModelChange={onFilterChange}
          disableRowSelectionOnClick
          loading={isLoading}
        />
      </Box>

      {editOpen === true ? (
        <UOMEdit openmodal={editOpen} itemId={itemID} onCloseModal={() => setEditOpen(false)} />
      ) : (
        ''
      )}
      {viewOpen === true ? (
        <UOMView openmodal={viewOpen} itemId={itemID} onCloseModal={() => setViewOpen(false)} />
      ) : (
        ''
      )}
      {deleteOpen === true ? (
        <UOMDelete
          openmodal={deleteOpen}
          itemId={itemID}
          onCloseModal={() => setDeleteOpen(false)}
        />
      ) : (
        ''
      )}
    </Box>
  );
}
